import type { FilterRuleModel } from '@/shared/generated'
import type z from 'zod'

import { zodResolver } from '@hookform/resolvers/zod'
import { format } from 'date-fns'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { FormProvider, RHFSelect } from '@/shared/components/HookForm'
import { Button } from '@/shared/components/ui/button'
import { FieldGroup, FieldSet } from '@/shared/components/ui/field'
import {
	Modal,
	ModalBody,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalTitle,
} from '@/shared/components/ui/modal'
import { ampli } from '@/shared/lib/ampli'
import { DateFormat } from '@/shared/tools/dates'

import { downloadAssortmentFile } from '../lib/helpers'
import { ExportSchema } from '../model/contract'

type FormData = z.infer<typeof ExportSchema>
type ExportAnchorAssortmentTableModalContentProps = {
	close(): void
	isOpen: boolean
	reportName?: string
	filters: FilterRuleModel[]
}
function ExportAnchorAssortmentTableModalContent({
	close,
	isOpen,
	reportName,
	filters,
}: ExportAnchorAssortmentTableModalContentProps) {
	const { t } = useTranslation()

	const methods = useForm<FormData>({
		resolver: zodResolver(ExportSchema),
		defaultValues: {
			report_type: 'full',
			file_format: 'xlsx',
			by_pricing_line: true,
		},
	})
	const _handleSubmit = ({ file_format, report_type }: FormData) => {
		ampli.anchoringExportDownload()
		const fileName = reportName
			? reportName.replace(/[^a-z0-9]/gi, '_').toLowerCase()
			: 'anchors_report'
		downloadAssortmentFile(
			{
				filters: filters || [],
				format: file_format as 'xlsx' | 'csv',
				//order_by: [],
			},
			`${fileName}_${format(new Date(), DateFormat.system)}.${file_format}`,
			t
		)
		close()
	}
	const handleSubmit = methods.handleSubmit(_handleSubmit)

	return (
		<Modal
			open={isOpen}
			onOpenChange={(open) => {
				if (!open) {
					close()
				}
			}}
		>
			<ModalContent>
				<ModalHeader>
					<ModalTitle>{t('app.download_report')}</ModalTitle>
				</ModalHeader>
				<ModalBody className='flex flex-col overflow-y-auto'>
					<FormProvider methods={methods} onSubmit={handleSubmit}>
						<FieldSet>
							<FieldGroup>
								<RHFSelect
									label={t('app.report_type')}
									className='w-56'
									name='report_type'
									options={[
										{
											value: 'full',
											label: t('app.all_columns'),
										},
									]}
								/>
								<RHFSelect
									className='w-56'
									label={t('app.file_format')}
									name='file_format'
									options={[
										{ value: 'xlsx', label: 'Excel' },
										{ value: 'csv', label: 'CSV' },
									]}
								/>
							</FieldGroup>
						</FieldSet>
					</FormProvider>
				</ModalBody>
				<ModalFooter className='justify-between space-x-2'>
					<Button onClick={close}>{t('general_cancel')}</Button>
					<Button variant='primary' onClick={handleSubmit}>
						{t('app.download_report')}
					</Button>
				</ModalFooter>
			</ModalContent>
		</Modal>
	)
}
export function ExportAnchorAssortmentTableModal(
	props: ExportAnchorAssortmentTableModalContentProps & { isOpen: boolean }
) {
	return <ExportAnchorAssortmentTableModalContent {...props} />
}
